/**
 * Разбор строк по группам — по ключу, а не по соседству.
 *
 * Раньше заголовок группы ставился там, где ключ очередной строки не совпал
 * с предыдущей. Это верно ровно до тех пор, пока строки отсортированы по полю
 * группировки, — а такой порядок держится только сразу после загрузки
 * с сервера. Дальше его ломает всё, что правит список в памяти:
 *
 * - заведённый кандидат встаёт первой строкой независимо от сортировки;
 * - строка, вставленная «как в Excel», встаёт по номеру, а не по группе;
 * - правка самого поля группировки меняет ключ строки, не двигая её с места.
 *
 * После любого из них одна группа разваливается на две: заголовок с тем же
 * названием появляется и наверху, и на своём прежнем месте. Причём цифры
 * в обоих оказываются одинаковые и неверные: у заголовков ключ идёт в `:key`
 * списка, а два одинаковых ключа Vue считает одной строкой и рисует поверх
 * первой то, что насчитал последней, — заказчик видел «Не заполнено,
 * 192 кандидата» дважды, хотя во второй группе строк было меньше.
 *
 * Сбор по ключу снимает это целиком и не зависит от того, каким путём список
 * поменяли: строка всегда оказывается в своей группе, ключ в списке
 * уникален, а счётчик считает то, что под заголовком и лежит.
 *
 * Порядок групп — по первой встреченной строке. Пока список пришёл
 * с сервера, это тот же порядок, что задала сортировка; заведённый кандидат
 * поднимает свою группу наверх — туда же, куда встаёт он сам.
 */

export interface GroupStatusShare {
  name: string;
  color: string;
  count: number;
}

export interface RowGroup {
  key: string;
  label: string;
  count: number;
  /** Разбивка по статусам для подписи заголовка */
  statuses: GroupStatusShare[];
  /** Номера строк в исходном списке, по порядку */
  rowIndices: number[];
}

interface DateSection {
  id: string;
  date: string;
  label: string;
  sortOrder: number;
}

interface DepartureRow {
  sectionId?: string | null;
}

export type DateGroupOrder = 'asc' | 'desc';

/** Сравниваем ISO-даты независимо от подписи и порядка создания. */
export function dateGroupComparator(
  sections: readonly DateSection[],
  _rows: readonly DepartureRow[],
  emptyKey: string,
  order: DateGroupOrder = 'asc',
): (left: string, right: string) => number {
  const dates = new Map(sections.map((section) => [section.id, section.date]));
  const ranks = new Map(sections.map((section) => [section.id, section.sortOrder]));
  return (left, right) => {
    if (left === right) return 0;
    if (left === emptyKey) return 1;
    if (right === emptyKey) return -1;
    const leftDate = dates.get(left) ?? '';
    const rightDate = dates.get(right) ?? '';
    if (leftDate !== rightDate) {
      if (!leftDate) return 1;
      if (!rightDate) return -1;
      return (leftDate < rightDate) === (order === 'asc') ? -1 : 1;
    }
    return (ranks.get(left) ?? 0) - (ranks.get(right) ?? 0) || (left < right ? -1 : 1);
  };
}

/** Пустые и заполненные группы занимают одно и то же место по своей дате. */
export function withDateSections(
  groups: readonly RowGroup[],
  sections: readonly DateSection[],
  rows: readonly DepartureRow[],
  emptyKey: string,
  order: DateGroupOrder = 'asc',
): RowGroup[] {
  const byKey = new Map(groups.map((group) => [group.key, group]));
  const addEmpty = (key: string, label: string) => {
    if (!byKey.has(key)) byKey.set(key, { key, label, count: 0, statuses: [], rowIndices: [] });
  };
  for (const section of sections) addEmpty(section.id, section.label);
  addEmpty(emptyKey, 'Без группы');
  const compare = dateGroupComparator(sections, rows, emptyKey, order);
  return [...byKey.values()].sort((left, right) => compare(left.key, right.key));
}

export function groupRowsByKey<T>(
  rows: readonly T[],
  keyOf: (row: T) => string,
  labelOf: (row: T) => string,
  statusOf: (row: T) => GroupStatusShare | null,
): RowGroup[] {
  const byKey = new Map<string, RowGroup>();

  rows.forEach((row, index) => {
    const key = keyOf(row);
    let group = byKey.get(key);
    if (!group) {
      // Подпись берётся у первой строки группы: у остальных она та же,
      // а пересчитывать её на каждую строку незачем
      group = { key, label: labelOf(row), count: 0, statuses: [], rowIndices: [] };
      byKey.set(key, group);
    }

    group.count += 1;
    group.rowIndices.push(index);

    // Разбивка по статусам считается на лету: данные уже в строках,
    // отдельный запрос ради счётчиков не нужен
    const status = statusOf(row);
    if (status) {
      const found = group.statuses.find((s) => s.name === status.name);
      if (found) found.count += 1;
      else group.statuses.push({ ...status, count: 1 });
    }
  });

  return [...byKey.values()];
}
